// src/edu-world/systems/world-impact-renderer.ts
// World impact: swaps wilted flowers image for blooming flowers with sparkle effect.

import Phaser from 'phaser';
import type { WorldImpact } from '../types';
import type { WorldImpactEvent } from '../types/events';
import type { EventBus } from '../core/event-bus';

export class WorldImpactRenderer {
  private scene: Phaser.Scene;
  private eventBus: EventBus;
  private impacts: WorldImpact[];
  private appliedImpacts: Set<string> = new Set();

  constructor(scene: Phaser.Scene, eventBus: EventBus, impacts: WorldImpact[]) {
    this.scene = scene;
    this.eventBus = eventBus;
    this.impacts = impacts;

    this.eventBus.on('world:impact', (event) => this.applyImpact(event));
  }

  private applyImpact(event: WorldImpactEvent): void {
    if (this.appliedImpacts.has(event.impact_id)) return;

    const impact = this.impacts.find((i) => i.id === event.impact_id);
    if (!impact) return;

    this.appliedImpacts.add(event.impact_id);

    // Find the wilted flowers image by data tag
    const targets = this.scene.children.list.filter(
      (child) => child.getData('element-id') === impact.target_element
    );

    for (const target of targets) {
      if (target instanceof Phaser.GameObjects.Image) {
        // Animate: scale up briefly, swap texture, sparkle
        this.scene.tweens.add({
          targets: target,
          scale: target.scale * 1.3,
          duration: 400,
          yoyo: true,
          ease: 'Back.easeOut',
          onStart: () => {
            // Swap texture to blooming flowers
            target.setTexture('flores-bonitas');
            target.setData('type', 'blooming-flower');
          },
        });

        // Sparkle particles around the flowers
        this.createSparkle(target.x, target.y);
      }
    }
  }

  private createSparkle(x: number, y: number): void {
    const sparkleCount = 8;
    for (let i = 0; i < sparkleCount; i++) {
      const angle = (i / sparkleCount) * Math.PI * 2;
      const sparkle = this.scene.add.circle(x, y, 4, 0xffd54f);
      sparkle.setDepth(12);
      sparkle.setAlpha(0.9);

      this.scene.tweens.add({
        targets: sparkle,
        x: x + Math.cos(angle) * 40,
        y: y + Math.sin(angle) * 40,
        alpha: 0,
        scale: 0.2,
        duration: 700,
        ease: 'Sine.easeOut',
        onComplete: () => sparkle.destroy(),
      });
    }
  }

  isApplied(impactId: string): boolean {
    return this.appliedImpacts.has(impactId);
  }
}
